import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/constants';
import { handleResponse, get, post, put, del, patch } from './base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { queryKeys } from './queryKeys';
import type {
  ApiResponse,
  CreateInstructorPayload,
  UpdateInstructorPayload,
  UpdateInstructorCommissionPayload
} from './types';
import {
  InstructorListResponse,
  InstructorDetails,
  InstructorCommission
} from '@/constants/Interfaces/CommonInterfaces';

export const useCreateNewInstructorMutation = () =>
  useMutation<ApiResponse, Error, CreateInstructorPayload>({
    mutationFn: (payload: CreateInstructorPayload) => {
      return post(
        `${API_ENDPOINTS.INSTRUCTORS}`,
        payload as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useEditInstructorMutation = () =>
  useMutation<ApiResponse, Error, UpdateInstructorPayload>({
    mutationFn: (payload: UpdateInstructorPayload) => {
      return put(`${API_ENDPOINTS.INSTRUCTORS}/${payload.id}`, payload.payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetInstructorListQuery = (payload: string) =>
  useQuery<InstructorListResponse>({
    queryFn: async () => {
      const response = await get<InstructorListResponse>(
        `${API_ENDPOINTS.INSTRUCTORS}?${payload}`
      );
      return handleResponse(response);
    },
    queryKey: queryKeys.instructor.list(payload),
    refetchOnWindowFocus: false
  });

export const useDeleteInstructorMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (id: number) => {
      return del(`${API_ENDPOINTS.INSTRUCTORS}/${id}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetInstructorDetailsQuery = (id: number) =>
  useQuery<InstructorDetails>({
    queryFn: async () => {
      const response = await get<InstructorDetails>(
        `${API_ENDPOINTS.INSTRUCTORS}/${id}`
      );
      return handleResponse(response);
    },
    queryKey: queryKeys.instructor.details(id),
    enabled: !!id,
    refetchOnWindowFocus: false
  });

export const useUpdateInstructorStatusMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (userId: number) => {
      return patch(`${API_ENDPOINTS.INSTRUCTORS}/${userId}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetInstructorCommissionQuery = (payload: string) =>
  useQuery<InstructorCommission[]>({
    queryFn: async () => {
      const response = await get<InstructorCommission[]>(
        `${API_ENDPOINTS.INSTRUCTOR_COMMISSION_RATES}?${payload}`
      );
      return handleResponse(response);
    },
    queryKey: queryKeys.instructor.commission(payload),
    refetchOnWindowFocus: false
  });

export const useUpdateInstructorCommissionMutation = () =>
  useMutation<ApiResponse, Error, UpdateInstructorCommissionPayload>({
    mutationFn: (payload: UpdateInstructorCommissionPayload) => {
      return post(
        `${API_ENDPOINTS.INSTRUCTOR_COMMISSION_RATES}`,
        payload as Record<string, unknown>
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });
